import { isoDateTime, z } from "../zod.js";
import { PublicPageLocale } from "../locales.js";
import { Uuid } from "../identity/admin-schemas.js";
import {
  AssignmentRole,
  CustomerStatus,
  CustomerTier,
  FulfillmentPlanEntry,
  PriceSource,
  SalesBusinessDate,
  SalesCurrency,
  SalesMinorMoney,
  SalesMinorMoneyNonNegative,
  SalesPct,
  SalesMeasure,
  SalesQty,
  SalesQtyOrZero,
  SalesSignedPct,
  SalesSignedQty,
  PreferredLocale,
  RequiredApprover,
  ShipmentStatus,
  DeliveredBy,
  OpportunityStatus,
} from "./schemas.js";

// Module 09 — Sales: the contract DTOs for slice P3-C, THE QUOTE CORE
// (spec 09 §2.12–§2.17, §3.3–§3.5, §5's quote/order/public rows), plus the
// four D-241 quote-slice deepenings the CRM benchmark assigned here:
// quote viewed-tracking, quote duplicate, the ATP
// traffic-light on composer lines, and per-override `min_qty`.
//
// WHAT IS ABSENT, AND WHY (D-026/D-033, the statement every slice writes):
// the analytics rollups (§2.21) are P3-E — the path stays out of
// openapi/v1.json entirely, so a 404 on a route that does not exist stays the
// honest answer. The notification LOG read (§2.20) LANDED with P3-E PR-E1
// (D-291; migration 0066): `CustomerNotificationListQuery` /
// `CustomerNotificationListResponse` beside `CustomerNotification`, whose
// `status` and `kind` widen with the dispatcher. This slice ships the
// order READ + credit/decide/cancel surface because the §3.5 accept
// transaction creates orders and an order you cannot open is a document the
// product loses. The direct-order create (`POST /v1/orders`) and its decide
// were P3-D's rows and LANDED on 2026-09-15 (P3-D PR-D1, D-283):
// `OrderCreateRequest` / `OrderDecideRequest` below, `OrderLine` widened with
// the eight §2.17 pricing facts (migration 0061), and `OrderDetailResponse`
// carrying the §3.6 `review` record while `pending_approval`. SHIPMENTS
// (§2.18/§2.19) LANDED on 2026-09-17 (P3-D PR-D3, D-286; migration 0063):
// the `Shipment*` / `PickPlan*` DTOs at the bottom of the order section,
// `OrderDetailResponse.shipments`, the two line counts on `OrderListItem`,
// and the `can_confirm_received` flag now server-computed rather than false.
//
// CONVENTIONS carried from schemas.ts, restated because they bite here:
//  * REDACTION IS OMISSION (§7 literal) — `unit_cost_minor`, `margin_pct` and
//    `margin_guard_pct` are `.optional()` on every line DTO, and the money
//    TOTALS are `.optional()` because `sales.read_value` gates them. Those
//    three are ALSO `.nullable()`, which is a DIFFERENT fact and must never
//    collapse into the first: absent = you may not see it, null = it does not
//    exist (no released cost basis). QuoteLine's own doc block argues it. The
//    booleans (`margin_breached`, `off_list`) stay REQUIRED: a sales user
//    without `sales.margin.read` reads "needs approval" and never a number
//    (§4.4). The web renders an omitted key as NOTHING, never a dash.
//  * THE PUBLIC PAYLOADS CARRY NO COST OR MARGIN AT ALL — not omitted-per-
//    role, ABSENT FROM THE SCHEMA (§4.10: "no cost/margin ever"). That is why
//    `PublicQuoteLine` is its own type rather than a pick of `QuoteLine`: a
//    field that does not exist cannot be leaked by a serializer change.
//  * MONEY is integer minor units + `currency char(3)`; quantities are exact
//    decimal strings (D-041).
//  * D-225: no server-minted prose. Every state the public pages render is a
//    typed `state` discriminant, every refusal a stable UPPER_SNAKE code.

// ---------------------------------------------------------------------------
// §2.12 quotes — enums
// ---------------------------------------------------------------------------

/** §2.12 `status` CHECK, in §3.4's order. `pending_approval` is the D-231
 * in-module review state — the name is deliberate so module 12's P4
 * absorption is additive and never a rename. */
export const QuoteStatus = z
  .enum([
    "draft",
    "pending_approval",
    "approved",
    "sent",
    "accepted",
    "converted",
    "rejected",
    "expired",
    "cancelled",
  ])
  .openapi("QuoteStatus");

export const QuoteOrigin = z.enum(["manual", "agent", "portal"]).openapi("QuoteOrigin");
export const QuoteAcceptedVia = z
  .enum(["token_link", "manual", "agent"])
  .openapi("QuoteAcceptedVia");
/** §3.4 / §5 `record-rejection`: the lost-reason codes §2.12 prefixes. */
export const QuoteRejectionReason = z
  .enum(["price", "lead_time", "stock", "other"])
  .openapi("QuoteRejectionReason");
/** §3.4 / §3.6 / §2.9 decide outcomes (D-231 — one grammar for all three). */
export const ReviewOutcome = z.enum(["approved", "rejected"]).openapi("ReviewOutcome");

/**
 * D-242's typed order enum for `GET /v1/quotes` (`-` prefix = descending).
 *
 * THE CARET SET IS THE HONEST SET, decided per column exactly as the customers
 * enum was, and the exclusions are the argument:
 *  - `total_minor` IS sortable, and ONLY for a caller holding
 *    `sales.read_value` (D-301). It is §7-redacted by omission for planner,
 *    storekeeper and qc, and those roles DO receive this list: the route is
 *    `sales.read`, and §4.5's narrower audience is the web mirror, which
 *    decides controls and never data (D-239 ruling 1). The cursor's anchor is
 *    the last row's sort value in plaintext, so an open money sort was a read
 *    of every total. The server answers 403 `missing_permission:
 *    sales.read_value` for both tokens to a caller without the grant, with
 *    the payload's own predicate. The enum stays as published (narrowing a
 *    shipped enum is a breaking change); the fence is the service's.
 *  - `status` and `origin` are raw enums under a translated chip: ordering by
 *    the wire token produces an order nobody reading the screen can predict.
 *    Both are filters instead.
 *  - `customer` is a cross-table display name; `revision_count` is derived.
 *    Neither is honest-cheap, so neither gets a caret (D-242's rule).
 *  - `valid_until` IS sortable and matters: the §4.5 red-under-3-days column
 *    is the one a manager triages by.
 */
export const QuotesOrder = z
  .enum([
    "-created_at",
    "created_at",
    "quote_no",
    "-quote_no",
    "updated_at",
    "-updated_at",
    "valid_until",
    "-valid_until",
    "total_minor",
    "-total_minor",
  ])
  .openapi("QuotesOrder");

// ---------------------------------------------------------------------------
// D-241 deepening: the ATP traffic-light
// ---------------------------------------------------------------------------

/**
 * The per-line availability verdict the §4.4 composer renders as a
 * traffic-light. PRESENTATION ONLY over calls the composer already makes (the
 * scope D-241 set): module 06's `/v1/inventory/atp` and module 05's
 * off-cut matcher.
 *
 * FOUR states, not three, because "we could not ask" is not amber:
 *   green    — ATP ≥ the line quantity at the fulfilling facility today.
 *   amber    — short from stock, but coverable (off-cut combo, or ATP within
 *              the line's wanted date once production is planned).
 *   red      — nothing covers it from stock and no combo exists.
 *   unknown  — the ATP read failed or its terms are `unavailable` (module 06
 *              §3.6 reports `qty_base:null` for terms owned by unbuilt
 *              modules). A light that renders GREEN on a failed read is worse
 *              than no light, so the failure has its own colourless state and
 *              the composer says so.
 */
export const AtpLight = z.enum(["green", "amber", "red", "unknown"]).openapi("AtpLight");

/** One line's fulfilment picture: the traffic-light plus the numbers behind
 * it, so a manager can see WHY it is amber without leaving the composer. */
export const LineFulfillmentState = z
  .object({
    light: AtpLight,
    /** ATP in the base unit, signed, 3 dp rounded down from module 06's 6 dp
     * (D-339). Null when `light='unknown'`, never a fake 0. */
    atp_qty: SalesSignedQty.nullable().openapi({
      description:
        "ATP at the fulfilling facility for the wanted date in the product's base unit, at 3 dp and signed: negative when holds exceed stock (spec 06 §3.6). Module 06 keeps 6 dp, and this figure is that one rounded toward negative infinity, so it never reads as more stock than the shelves hold. The light is judged on the unrounded figure. Null when `light` is `unknown`, never a fake 0 (D-339).",
    }),
    /** True when module 06 reported at least one term as `unavailable`
     * (po_inbound/wo_planned_output). The composer states it beside the
     * light rather than letting a partial total read as the whole truth. */
    partial_terms: z.boolean(),
    /** Off-cut options module 05 ranked for this line (§3.3), best first. */
    offcut_options: z
      .array(
        z
          .object({
            /** The §2.10 class that would price it. */
            offcut_class_id: Uuid,
            offcut_class_code: z.string(),
            discount_pct: SalesPct,
            /** The named pieces — "43 m + 57 m", the whole point (§3.3). */
            pieces: z
              .array(
                z
                  .object({
                    stock_unit_id: Uuid,
                    unit_code: z.string(),
                    // Module 06's own numeric(18,6) — what the server has
                    // always SENT here; the narrower declaration was a lie a
                    // response validator would have caught (D-265).
                    length: SalesMeasure,
                    location_code: z.string().nullable(),
                  })
                  .strict(),
              )
              .min(1)
              .max(4),
            /** What applying it would do to the line, in minor units — the
             * saving is the sentence the manager acts on. */
            unit_price_minor: SalesMinorMoneyNonNegative,
            line_saving_minor: SalesMinorMoneyNonNegative,
          })
          .strict(),
      )
      .max(5),
    /** D-301 round 7. Optional and additive, so no client that predates it
     * breaks. */
    offcut_options_withheld: z.literal(true).optional().openapi({
      description:
        "D-301: present, and true, only when module 06 found at least one off-cut combination for the line and this caller is served none of them because it may not see their prices. That is a caller without `sales.read_value`, or one the pricing door refuses (a lineage outside POST /v1/pricing/resolve's, or a sales-only principal not assigned to the customer). `offcut_options` is then empty. Absent means nothing was withheld from this caller.",
    }),
  })
  .openapi("LineFulfillmentState");

export const FulfillmentOptionsRequest = z
  .object({
    customer_id: Uuid,
    product_id: Uuid,
    qty: SalesQty,
    uom_id: Uuid,
    wanted_date: SalesBusinessDate,
    /** Which facility's shelves to ask about. Absent = the tenant's default
     * fulfilling facility (§2.16's `fulfilling_facility_id` default). */
    facility_id: Uuid.optional(),
  })
  .strict()
  .openapi("FulfillmentOptionsRequest");

export const FulfillmentOptionsResponse = LineFulfillmentState.openapi(
  "FulfillmentOptionsResponse",
);

// ---------------------------------------------------------------------------
// §2.14 quote lines
// ---------------------------------------------------------------------------

export const QuoteLine = z
  .object({
    id: Uuid,
    line_no: z.number().int().min(1),
    product_id: Uuid,
    /** §2.14 — the SNAPSHOT taken at write time ("КГ 1×70 чёрный"), not a
     * live join: a quote must read the same in five years. */
    description: z.string(),
    qty: SalesQty,
    uom_id: Uuid,
    /** NULLABLE, and the two facts below are DIFFERENT facts (§2.14 is amended
     * in this PR to match — see the migration's SPEC AMENDMENT block). The
     * shipped §3.2 engine returns `listPriceMinor: null` on the manual-price
     * path §3.2 step 1 authorises: no list row was found, the manager typed a
     * price, the line resolves `manual` with `off_list=true` and there is no
     * list price to show. `null` is the honest answer; the alternative would be
     * echoing the manual price back as though a list had produced it.
     *
     * Both prices are §7 money since D-337 ruling 1, the order line's rule:
     * ABSENT for a reader without `sales.read_value`, beside
     * `line_total_minor`. Absent means the reader may not have it, and null
     * means no list row priced the line, so the two never collapse. */
    list_price_minor: SalesMinorMoneyNonNegative.nullable().optional(),
    unit_price_minor: SalesMinorMoneyNonNegative.optional(),
    price_source: PriceSource,
    price_source_ref: Uuid.nullable(),
    offcut_class_id: Uuid.nullable(),
    fulfillment_plan: z.array(FulfillmentPlanEntry),
    /** BOTH `.optional()` AND `.nullable()`, and the two carry DIFFERENT
     * meanings that must never collapse into one:
     *   * ABSENT (the key is not on the payload) = §7 redaction — this reader
     *     lacks `sales.margin.read`. Omission, never a null, never a dash.
     *   * PRESENT AND NULL = the value genuinely does not exist: no released
     *     standard cost for the product, no UoM conversion path, or a
     *     cross-currency cost (§9.13's no-silent-FX rule on the cost side).
     *     An unprovable margin is not 0.00 — writing 0.00 would read as a
     *     100%-cost line to every §4.6 reviewer. The guard verdict already
     *     fails CLOSED in that state (`margin_breached=true`), so the honest
     *     null costs no safety.
     * A reader that sees `null` knows the number does not exist; a reader that
     * sees nothing knows it may not have it. Collapsing them would make "no
     * margin" and "may not see margin" the same wire fact. */
    unit_cost_minor: SalesMinorMoneyNonNegative.nullable().optional(),
    margin_pct: SalesSignedPct.nullable().optional(),
    margin_guard_pct: SalesPct.nullable().optional(),
    /** ALWAYS present — the unprivileged composer's "needs approval" flag. */
    margin_breached: z.boolean(),
    off_list: z.boolean(),
    line_total_minor: SalesMinorMoneyNonNegative.optional(),
    /** §2.14 — the CTP quick-check hint: the promised date of the quoted
     * promise the composer's Check date wrote (P3-J J-2, D-465 ruling 5).
     * Null when the line was saved without one; non-binding until the
     * quote is accepted and module 03 commits the promise. */
    promise_date_hint: SalesBusinessDate.nullable(),
    /** Module 03's quoted promise behind the hint (J-2); null with it. */
    promise_id: Uuid.nullable(),
  })
  .openapi("QuoteLine");

/** The composer's write shape. `unit_price_minor` ABSENT means "resolve it"
 * (§3.2); PRESENT means a manual price, which is what sets `off_list` and can
 * trip the §2.11 guard — the server decides both, never the client. */
export const QuoteLineInput = z
  .object({
    product_id: Uuid,
    qty: SalesQty,
    uom_id: Uuid,
    unit_price_minor: SalesMinorMoneyNonNegative.optional(),
    fulfillment_plan: z.array(FulfillmentPlanEntry).max(10).optional(),
    /** The quoted promise Check date wrote for this line (P3-J J-2, D-465
     * ruling 5). The save checks it with module 03 and writes its date as
     * `promise_date_hint`; absent means the line carries no date. */
    promise_id: Uuid.optional(),
  })
  .strict()
  .openapi("QuoteLineInput");

// ---------------------------------------------------------------------------
// §2.13 revisions + §2.12 head
// ---------------------------------------------------------------------------

export const QuoteRevision = z
  .object({
    id: Uuid,
    revision_no: z.number().int().min(1),
    /** §7 OMISSION on the three money totals — `sales.read_value`. */
    subtotal_minor: SalesMinorMoneyNonNegative.optional(),
    tax_minor: SalesMinorMoneyNonNegative.optional(),
    total_minor: SalesMinorMoneyNonNegative.optional(),
    currency: SalesCurrency,
    /** D-234: which VAT convention the resolved list carries, so the composer
     * can label the tax line honestly instead of assuming exclusive. */
    vat_mode: z.enum(["inclusive", "exclusive"]),
    /** The rate the tax line was computed at (§2.13 — D-022 tenant seed
     * default until module 11's tax config lands). */
    vat_rate_pct: SalesPct,
    note_to_customer: z.string().nullable(),
    internal_note: z.string().nullable(),
    /** The offer agent's note on its draft and the note's locale (spec 09
     * §2.12, P3-K K-3, D-516 ruling 4): null on every revision a person
     * wrote, never staff text, and rendered inside the untrusted frame.
     * Optional on the wire so the change is additive (D-550). */
    agent_note: z.string().max(500).nullable().optional(),
    agent_note_locale: z.string().max(10).nullable().optional(),
    created_by: Uuid,
    created_at: isoDateTime(),
    lines: z.array(QuoteLine),
  })
  .openapi("QuoteRevision");

/** One row of `GET /v1/quotes` — the §4.5 column set exactly, plus the D-241
 * viewed stamps. `customer_display_name` is denormalized onto the row on
 * purpose: the alternative is N+1 reads from a list screen. */
export const QuoteListItem = z
  .object({
    id: Uuid,
    quote_no: z.string(),
    customer_id: Uuid,
    customer_display_name: z.string(),
    customer_tier: CustomerTier,
    owner_user_id: Uuid,
    origin: QuoteOrigin,
    status: QuoteStatus,
    currency: SalesCurrency,
    /** §7 OMISSION — `sales.read_value`. */
    total_minor: SalesMinorMoneyNonNegative.optional(),
    valid_until: SalesBusinessDate.nullable(),
    current_revision_no: z.number().int().min(1),
    sent_revision_no: z.number().int().min(1).nullable(),
    /** §4.5's revision-count column — `current_revision_no` IS the count
     * (revisions are 1..n with no gaps), carried separately anyway so the
     * column never has to know that invariant. */
    revision_count: z.number().int().min(1),
    /** True when ANY line of the current revision is off-list — the §4.5
     * off-list-only filter's row-side fact. */
    has_off_list_line: z.boolean(),
    /** True when any line breached a guard and the class did not bypass. */
    has_margin_breach: z.boolean(),
    // --- D-241 deepening: viewed-tracking -------------------------------
    /** First/last time the customer's token page resolved (§3.5.1 stamps it
     * in the same txn as the read). NULL until the customer opens the link —
     * which is the signal: `sent` + never viewed is a different sales
     * situation from `sent` + viewed twice yesterday. */
    first_viewed_at: isoDateTime().nullable(),
    last_viewed_at: isoDateTime().nullable(),
    created_at: isoDateTime(),
    updated_at: isoDateTime(),
    // --- #293: the deal facts a picker judges a row by ---------------------
    /** The deal this quote belongs to (§2.12 `opportunity_id`), served by the
     * detail's rule (D-312 ruling 21): the id when the reader's deal door
     * (§7 narrowing 15) answers that deal, NULL when the quote names none or
     * the door refuses it. Sent on every row since #293 and optional on the
     * wire only so a client reads a server one deploy behind. */
    opportunity_id: Uuid.nullable().optional(),
    /** Whether the quote belongs to ANY deal, whether or not the reader's deal
     * door answers it. §7 OMISSION: present only for a holder of
     * `sales.opportunity.write`, the one reader the quote link door already
     * tells this fact on every quote its quote door answers (an unlink answers
     * 200 for a quote on no deal and 404 for one on a deal it cannot write,
     * and a link answers 409 `QUOTE_LINKED_ELSEWHERE`). With `opportunity_id`
     * NULL and this true, the quote sits on a deal the reader cannot open, and
     * no picker offers it. Carries no id, number, title or status of that
     * deal. */
    opportunity_linked: z.boolean().optional(),
  })
  .openapi("QuoteListItem");

export const QuoteListResponse = z
  .object({ items: z.array(QuoteListItem), next_cursor: z.string().nullable() })
  .openapi("QuoteListResponse");

/** The live accept link's state, as the OFFICE sees it (never the raw token —
 * the token is the credential and the office does not need it to work). */
export const QuoteTokenState = z
  .object({
    /** Present only right after a send, so the composer can offer "copy link"
     * once. A re-read of the quote never re-serves it. */
    accept_url: z.url().optional(),
    expires_at: isoDateTime().nullable(),
    revoked: z.boolean(),
    used_at: isoDateTime().nullable(),
    revision_no: z.number().int().min(1).nullable(),
  })
  .openapi("QuoteTokenState");

/**
 * The deal a quote belongs to, as the quote detail serves it (spec 09 §2.12
 * `opportunity_id`, §5's amended detail row; P3-E2 PR E2-1, D-312 rulings 4
 * and 21). Four fields and no money: enough for the composer's Deal chip and
 * the quote peek, and nothing a deal door would withhold.
 */
export const QuoteOpportunityRef = z
  .object({
    id: Uuid,
    opportunity_no: z.string(),
    title: z.string(),
    status: OpportunityStatus,
  })
  .openapi("QuoteOpportunityRef");

export const QuoteDetailResponse = z
  .object({
    id: Uuid,
    quote_no: z.string(),
    customer_id: Uuid,
    customer_display_name: z.string(),
    customer_tier: CustomerTier,
    customer_locale: PreferredLocale,
    contact_id: Uuid.nullable(),
    owner_user_id: Uuid,
    /** §2.2 `customer_assignments` for this quote's customer — who else may
     * send it (§3.4's "sales (owner or collaborator)"). The composer shows
     * them; the 360's assignments panel edits them. */
    collaborator_user_ids: z.array(Uuid),
    origin: QuoteOrigin,
    /** The §4.4 agent-draft banner's subject — module 17's inbound message
     * that spawned this quote. NULL for manual quotes. */
    source_message_id: Uuid.nullable(),
    /** The inbound text itself, rendered INLINE in the banner (§4.4). Absent
     * when there is no source message, or when the reader may not read
     * module 17 conversations — an omission, not an empty string. */
    source_message_text: z.string().optional(),
    currency: SalesCurrency,
    status: QuoteStatus,
    validity_days: z.number().int().min(1).max(90),
    valid_until: SalesBusinessDate.nullable(),
    current_revision_no: z.number().int().min(1),
    sent_revision_no: z.number().int().min(1).nullable(),
    /** Whether `GET /v1/quotes/{id}/pdf` will serve a document (D-288, D-118:
     * a control exists only when it works) — true when the most recent send of
     * the sent revision stored a PDF. False on a quote never sent, and on one
     * sent before migration 0064 gave a send its `pdf_file_id`. */
    pdf_available: z.boolean(),
    /** This quote's customer as §3.1.4 left them (`active` | `archived`).
     * Served so the composer can NAME the reason a write door is missing
     * rather than say only that one is: an archived customer's quotes stay
     * readable, and until this landed the screen showed no sign at all that
     * the book had been closed. Never redacted — the customer list already
     * carries the same column for every reader of `sales.read`. */
    customer_status: CustomerStatus,
    /** Whether a door that mints a quote DOCUMENT for this quote's OWN
     * customer — `POST /v1/quotes/{id}/revisions` (§4.4's "Save as a new
     * version") and `POST /v1/quotes/{id}/duplicate` — would pass the
     * target-customer checks for THIS caller (task #229; the `requotable`
     * pattern of P3-D PR-D5, D-289, itself the `facility_writable` shape of
     * D-118): the customer is not archived and, for a sales-narrowed caller,
     * is assigned to them (`targetCustomerRefusal`).
     *
     * BOTH doors call `requireCustomer` as their FIRST act, before the status
     * arm each of them owns, so one flag answers for both and neither can
     * drift from it. Without it §4.4 offered Duplicate at every status and
     * Save on every non-terminal one over an archived customer's quote, and
     * each press answered 422 CUSTOMER_ARCHIVED — or 404 CUSTOMER_NOT_FOUND
     * for a rep who still OWNS the quote after being unassigned from its
     * customer, which the quote's own `owner_user_id OR assigned` read admits
     * and this rule does not.
     *
     * It says nothing about `sales.quote.write`, which the client reads from
     * the role mirror, and nothing about the quote's STATUS, which
     * `quote-state.ts` answers. */
    customer_quotable: z.boolean(),
    rejected_reason: z.string().nullable(),
    accepted_at: isoDateTime().nullable(),
    accepted_via: QuoteAcceptedVia.nullable(),
    converted_order_id: Uuid.nullable(),
    converted_order_no: z.string().nullable(),
    /** The deal this quote belongs to (§2.12 `opportunity_id`, E2-1). NULL
     * when the quote names no deal, AND when it names one the deal door would
     * not answer this reader (§7 narrowing 15, D-312 ruling 21: a quote's own
     * door is wider than its deal's for a sales principal who owns the quote
     * but neither owns the deal nor is assigned the customer). The quote
     * itself still reads. */
    opportunity: QuoteOpportunityRef.nullable(),
    /** Whether the quote belongs to ANY deal (#293), the list row's
     * `opportunity_linked` and served by its rule: present only for a holder
     * of `sales.opportunity.write`. With `opportunity` NULL and this true, the
     * quote sits on a deal the reader cannot open, so the composer offers no
     * Link and says so instead of "No deal". */
    opportunity_linked: z.boolean().optional(),
    first_viewed_at: isoDateTime().nullable(),
    last_viewed_at: isoDateTime().nullable(),
    /** How many times the token page resolved — the funnel's raw count. */
    view_count: z.number().int().min(0),
    created_at: isoDateTime(),
    updated_at: isoDateTime(),
    /** The CURRENT revision in full (head + lines). Older revisions come from
     * `GET /v1/quotes/{id}/revisions` — the detail read stays one round trip
     * for the screen that has to open fast. */
    revision: QuoteRevision,
    token: QuoteTokenState,
    /** The §3.4 review record — served ONLY while the review is OPEN
     * (`pending_approval`, all four fields null, meaning "a review is open")
     * and OMITTED once decided (D-255): §2.12 carries no decision columns, so
     * the decision lives in `sales.quote_review_decided` and the audit chain
     * until module 12 owns the surface. An object of four nulls after the fact
     * would be a record that exists and knows nothing. */
    review: z
      .object({
        outcome: ReviewOutcome.nullable(),
        decided_by: Uuid.nullable(),
        decided_at: isoDateTime().nullable(),
        note: z.string().nullable(),
      })
      .optional(),
  })
  .openapi("QuoteDetailResponse");

export const QuoteRevisionListResponse = z
  .object({ items: z.array(QuoteRevision) })
  .openapi("QuoteRevisionListResponse");

// ---------------------------------------------------------------------------
// §5 quote write requests
// ---------------------------------------------------------------------------

/** §5 `POST /v1/quotes`. `opportunity_id` (P3-E2 PR E2-1, D-312 ruling 4)
 * links the new draft to an open deal of the same customer at create — the
 * composer opened from a deal page, or its Deal control. A link is a deal
 * write: naming it requires `sales.opportunity.write` as well, refused 403
 * naming that code before the `Q` counter is taken. The door then takes the
 * counter and inserts the quote, reads the deal `FOR SHARE` through §7
 * narrowing 16's write door, and refuses 404 `OPPORTUNITY_NOT_FOUND`, 409
 * `OPPORTUNITY_CUSTOMER_MISMATCH` and 409 `OPPORTUNITY_NOT_OPEN`, in that
 * order. A duplicate or a requote never carries a link. */
export const QuoteCreateRequest = z
  .object({
    customer_id: Uuid,
    contact_id: Uuid.nullish(),
    lines: z.array(QuoteLineInput).min(1).max(200),
    validity_days: z.number().int().min(1).max(90).optional(),
    origin: QuoteOrigin.optional(),
    source_message_id: Uuid.nullish(),
    opportunity_id: Uuid.optional(),
    note_to_customer: z.string().max(4000).nullish(),
    internal_note: z.string().max(4000).nullish(),
  })
  .strict()
  .openapi("QuoteCreateRequest");

/** §5 `POST /v1/quotes/{id}/revisions` — the FULL line payload, never a patch:
 * a revision is a new immutable document, and a partial edit against a
 * repriced list is how two managers disagree about what was quoted (§9.3). */
export const QuoteRevisionCreateRequest = z
  .object({
    lines: z.array(QuoteLineInput).min(1).max(200),
    validity_days: z.number().int().min(1).max(90).optional(),
    note_to_customer: z.string().max(4000).nullish(),
    internal_note: z.string().max(4000).nullish(),
    contact_id: Uuid.nullish(),
  })
  .strict()
  .openapi("QuoteRevisionCreateRequest");

export const QuoteSendRequest = z
  .object({
    /** §5 "channel override?" — absent uses the customer's preferred channel
     * (§2.1 `preferred_channel`). */
    channel: z.enum(["telegram", "whatsapp", "email"]).optional(),
    contact_id: Uuid.nullish(),
  })
  .strict()
  .openapi("QuoteSendRequest");

/**
 * §3.4's send answer, as a DISCRIMINATED UNION on the resulting status rather
 * than a 200 whose meaning the client infers from which keys are present.
 * A send that entered review is not a failure — it is the other legal
 * outcome, and §4.4's button copy changes on it.
 */
export const QuoteSendResponse = z
  .discriminatedUnion("status", [
    z
      .object({
        status: z.literal("sent"),
        revision_no: z.number().int().min(1),
        valid_until: SalesBusinessDate,
        /** The tokenized customer link (§4.10), served ONCE, on the send, and
         * only to a sender holding `sales.read_value` (D-337 ruling 2). The
         * link opens the customer's priced offer, so a sender who may not
         * read the prices is not handed the door to them. */
        accept_url: z.url().optional(),
        accept_expires_at: isoDateTime(),
        /** The PDF that went out WITH this send (D-288 ruling 1) — stored on
         * the send's token row, so a re-send carries its own document with
         * its own validity date. Non-null on every send since migration 0064.
         * It stays NULLABLE on the wire on purpose: narrowing a published
         * nullable is a breaking change, and the office reads the document
         * through `GET /v1/quotes/{id}/pdf`, never through this id directly
         * (`GET /v1/files/{id}` answers 409 FILE_NOT_DIRECTLY_READABLE). */
        pdf_file_id: Uuid.nullable(),
        /** The module-17 dispatch this send rode (§3.4). NULL when the
         * customer has no messaging channel and the link was handed over
         * out of band. */
        message_id: Uuid.nullable(),
      })
      .strict(),
    z
      .object({
        status: z.literal("pending_approval"),
        revision_no: z.number().int().min(1),
        /** WHY it went to review — the §4.6 card's own summary, returned to
         * the sender so the composer can say it without a second read. */
        breaches: z.array(
          z
            .object({
              line_no: z.number().int().min(1),
              product_id: Uuid,
              /** §7 OMISSION: the numbers only for `sales.margin.read`.
               *
               * OPTIONAL BUT NOT NULLABLE — deliberately unlike `QuoteLine`'s
               * three margin fields, which are BOTH. A breach entry is a CARD
               * SUMMARY, not the line: it exists to say which line sent the
               * quote to review, and `line_no` + `off_list` carry that with no
               * number at all. So here a margin that does not EXIST (no
               * released cost basis) is OMITTED exactly as one this reader may
               * not SEE is; `ReviewCard.breach_lines` below is the same shape
               * for the same reason, and the serializer behind it spreads each
               * key conditionally to honour it. A serializer that instead
               * spreads `margin_pct: line.marginPct` unconditionally puts a
               * `null` on a payload that this contract, `openapi/v1.json` and
               * every generated client type as `string | undefined`. */
              margin_pct: SalesSignedPct.optional(),
              guard_pct: SalesPct.optional(),
              delta_minor: SalesMinorMoney.optional(),
              off_list: z.boolean(),
            })
            .strict(),
        ),
      })
      .strict(),
  ])
  .openapi("QuoteSendResponse");

export const QuoteDecideRequest = z
  .object({
    outcome: ReviewOutcome,
    /** §5: mandatory on reject. Enforced in the service (a conditional
     * requirement is not expressible as a zod object shape without turning
     * the DTO into a union the client would have to discriminate). */
    note: z.string().max(2000).nullish(),
  })
  .strict()
  .openapi("QuoteDecideRequest");

export const QuoteCancelRequest = z
  .object({ reason: z.string().min(1).max(2000) })
  .strict()
  .openapi("QuoteCancelRequest");

export const QuoteRecordRejectionRequest = z
  .object({
    reason_code: QuoteRejectionReason,
    note: z.string().max(2000).nullish(),
  })
  .strict()
  .openapi("QuoteRecordRejectionRequest");

export const QuoteAcceptManualRequest = z
  .object({
    /** §5: the phone deal's own note, audited with the acceptance. */
    note: z.string().max(2000).nullish(),
  })
  .strict()
  .openapi("QuoteAcceptManualRequest");

/**
 * D-241 deepening: DUPLICATE (the CRM benchmark research, D-241: "Odoo
 * quote-template value without a template library").
 *
 * The new quote is a DRAFT for the same or another customer, REPRICED at
 * current lists — never a copy of pinned prices, because a copied price is a
 * price nobody resolved. The §9.3 "prices refreshed" diff is what the
 * composer renders on arrival, so the manager sees what moved.
 */
export const QuoteDuplicateRequest = z
  .object({
    /** Absent = same customer. Present = re-quote the same basket for
     * someone else, which is the distributor-desk motion this exists for. */
    customer_id: Uuid.optional(),
    contact_id: Uuid.nullish(),
    /** Carry the manual (off-list) prices across instead of repricing. The
     * default is FALSE — repricing is the honest default — and choosing true
     * still re-runs the guard, so a carried price can land in review. */
    keep_manual_prices: z.boolean().optional(),
  })
  .strict()
  .openapi("QuoteDuplicateRequest");

/**
 * Requote-from-order (P3-D PR-D5, D-289 rulings 6-8): a new DRAFT quote from
 * an order's surviving lines, repriced through §3.2 at the tenant's today —
 * the "reorder from history" of the CRM benchmark research (D-241). The same three
 * knobs as the quote duplicate and nothing else: the order is only READ.
 * No fulfilment plan is ever carried (ruling 7): a plan copied from an order
 * names pieces that order has shipped or holds, and §3.2 would price an
 * off-cut line at its discount without checking the pieces exist.
 */
export const OrderRequoteRequest = z
  .object({
    /** Absent = the order's customer. Present = the same basket for another
     * customer, under the same assignment and currency rules as a create. */
    customer_id: Uuid.optional(),
    contact_id: Uuid.nullish(),
    /** Carry the order lines' manual (off-list) prices across instead of
     * repricing; the §2.11 guard still runs on each. Default FALSE. TRUE needs
     * `sales.read_value` (D-301 round 6): the carried price lands on a quote
     * line, which every `sales.read` holder is served. */
    keep_manual_prices: z.boolean().optional(),
  })
  .strict()
  .openapi("OrderRequoteRequest");

export const QuoteDuplicateResponse = z
  .object({
    quote_id: Uuid,
    quote_no: z.string(),
    /** §9.3's diff, per line that moved. Empty array = nothing repriced. */
    repriced: z.array(
      z
        .object({
          line_no: z.number().int().min(1),
          product_id: Uuid,
          old_unit_price_minor: SalesMinorMoneyNonNegative.optional(),
          new_unit_price_minor: SalesMinorMoneyNonNegative.optional(),
        })
        .strict(),
    ),
    /** Lines the source quote had that could not be repriced at all (the
     * product left the list, §3.2 PRICE_NOT_LISTED). They are DROPPED from
     * the duplicate and named here — a silently shorter quote is the defect
     * this field exists to prevent. */
    dropped: z.array(z.object({ line_no: z.number().int().min(1), product_id: Uuid }).strict()),
  })
  .openapi("QuoteDuplicateResponse");

/** §5 remind/expire — the §4.5 bulk-bar verbs, one quote per call (the bar
 * fans out client-side over the SELECTED rows, never over "all N matching"). */
export const QuoteRemindResponse = z
  .object({
    quote_id: Uuid,
    /** NULL when the customer has no messaging channel — a reminder nobody
     * could be sent is reported, not counted as sent. */
    message_id: Uuid.nullable(),
    next_allowed_at: isoDateTime(),
  })
  .openapi("QuoteRemindResponse");

export const QuoteExpireResponse = z
  .object({ quote_id: Uuid, status: QuoteStatus })
  .openapi("QuoteExpireResponse");

// ---------------------------------------------------------------------------
// D-241 deepening: customer assignments (the flagged §5 omission)
// ---------------------------------------------------------------------------

/**
 * §2.2 `customer_assignments` has carried `role='collaborator'` since P3-A and
 * §3.4 grants collaborators send rights — with no route to create one. Two
 * endpoints close it; the §4.2 assignments panel is their surface.
 *
 * THE LAST-OWNER GUARD is a contract fact, not a UI nicety: a customer with no
 * owner has no `owner_user_id` for §4.5's filter, no addressee for §3.6's
 * follow-up tasks and no one the review card names as requester. The remove
 * route refuses `CANNOT_REMOVE_LAST_OWNER` and the panel discloses it BEFORE
 * the press.
 */
/** `POST /v1/customers/{id}/assignments`. Since P3-E2 PR E2-2a1 (D-326 ruling
 * 3) it takes an optional, nullable `expected_owner_user_id`: the owner the
 * caller saw, checked under the customer's row lock, so a hand-over another
 * admin committed meanwhile answers 409 `OWNER_CHANGED` instead of being
 * overwritten. Absent means no check, the pre-E2-2 behaviour, and `null`
 * is the expectation that the customer has no owner, which the bulk bar sends
 * for a row whose loaded `owner_user_id` is null (spec 09 §5's E2-2 row 22a). */
export const CustomerAssignmentCreateRequest = z
  .object({
    user_id: Uuid,
    role: AssignmentRole,
    expected_owner_user_id: Uuid.nullable().optional(),
  })
  .strict()
  .openapi("CustomerAssignmentCreateRequest");

export const CustomerAssignmentDeleteResponse = z
  .object({ deleted: z.literal(true) })
  .openapi("CustomerAssignmentDeleteResponse");

// ---------------------------------------------------------------------------
// §2.16 / §2.17 orders — the READ surface this slice owns
// ---------------------------------------------------------------------------

export const OrderStatus = z
  .enum([
    "pending_approval",
    "confirmed",
    "in_production",
    "ready",
    "shipped",
    "delivered",
    "closed",
    "cancelled",
  ])
  .openapi("OrderStatus");

export const CreditCheckStatus = z
  .enum(["passed", "blocked_credit", "blocked_prepay", "override_approved"])
  .openapi("CreditCheckStatus");

export const FulfillmentMode = z
  .enum(["stock", "offcut_combo", "production", "mixed"])
  .openapi("FulfillmentMode");

/** §4.7's at-risk reasons (P3-J J-3, D-466): worst first. */
export const OrderAtRiskReason = z
  .enum(["promise_broken", "promise_at_risk", "date_passed"])
  .openapi("OrderAtRiskReason");

/** Module 03's risk reasons as the order line mirrors them (0103's CHECK). */
export const PromiseRiskReasonKey = z
  .enum(["stock_consumed", "material_delay", "capacity_loss", "schedule_slip"])
  .openapi("OrderLinePromiseRiskReason");

export const OrderLineStatus = z
  .enum(["open", "in_production", "ready", "shipped", "cancelled"])
  .openapi("OrderLineStatus");

/** D-242's order enum for `GET /v1/orders`. `-confirmed_at` is the default:
 * the list's question is "what did we just take on". `promise_date` gets NO
 * caret in P3 — module 03 is absent, every value is NULL, and a caret on a
 * column of nulls is a control that cannot do anything (D-033). It arrives
 * with 03. `total_minor` / `-total_minor` answer only a caller holding
 * `sales.read_value` (D-301): §7 omits the total from planner, qc and
 * storekeeper, who all read this list, and the cursor's anchor would hand the
 * total back. Refused 403 `missing_permission: sales.read_value` otherwise. */
/**
 * The open orders import an order came from (D-353 ruling 5). `imported` is
 * served to every reader; `imported_from_job_id` only to a reader whose
 * import door answers the job, and null otherwise. Optional on the wire only
 * for a server one deploy behind.
 */
function orderImportFields() {
  return {
    imported: z.boolean().optional(),
    imported_from_job_id: Uuid.nullable().optional(),
  };
}

export const OrdersOrder = z
  .enum([
    "-confirmed_at",
    "confirmed_at",
    "order_no",
    "-order_no",
    "created_at",
    "-created_at",
    "total_minor",
    "-total_minor",
  ])
  .openapi("OrdersOrder");

export const OrderListItem = z
  .object({
    id: Uuid,
    order_no: z.string(),
    customer_id: Uuid,
    customer_display_name: z.string(),
    /** The customer's own PO number as the order was placed with it (§2.16),
     * or NULL. On the list since P3-D PR-D5 (D-289 ruling 5) so the
     * duplicate-PO warning is actionable and the orders CSV carries the number
     * finance reconciles against. Not a value field — every reader sees it. A
     * REQUIRED key ADDED to a non-union response, which `oasdiff` classes as
     * non-breaking; every typed fixture must carry it. */
    customer_po_ref: z.string().nullable(),
    owner_user_id: Uuid,
    quote_id: Uuid.nullable(),
    fulfilling_facility_id: Uuid,
    status: OrderStatus,
    credit_check_status: CreditCheckStatus,
    currency: SalesCurrency,
    total_minor: SalesMinorMoneyNonNegative.optional(),
    /** NULL in P3 — module 03's CTP is absent (§3.5d). The list renders the
     * stated pending in place of the column, never a column of blanks. */
    promise_date: SalesBusinessDate.nullable(),
    /** §4.7's at-risk flag (amended for P3-J J-3, D-466): a live line's date
     * has passed while it is still to make, or module 03's mirror on a live
     * line reads `at_risk` or `broken`. Never on a cancelled or closed order. */
    at_risk: z.boolean(),
    /** Why `at_risk`, the worst reason on the order; null when it is false. */
    at_risk_reason: OrderAtRiskReason.nullable(),
    confirmed_at: isoDateTime().nullable(),
    created_at: isoDateTime(),
    /** The timestamp of the CURRENT status (§4.7 "status chip with
     * timestamp") — whichever of the §2.16 stamps the status names. */
    status_at: isoDateTime().nullable(),
    /**
     * §4.8's ship-queue badge (P3-D PR-D3, D-286): "lines count, all-reserved
     * indicator" as two integers the floor renders as `reserved/total`.
     * `lines_total` counts the lines STILL TO PICK (non-cancelled, with
     * ordered − shipped − live picks on open shipments above zero);
     * `lines_reserved` counts those among them holding at least one ACTIVE
     * module-06 reservation — module 06's answer, never the line's own
     * `reservation_ids`, which is append-only history (D-286 ruling 28). Two
     * REQUIRED integers ADDED to a published response, which `oasdiff` classes
     * as non-breaking (the `OrderLine` pricing-fact note above); every typed
     * fixture must carry them.
     */
    lines_total: z.number().int().nonnegative(),
    lines_reserved: z.number().int().nonnegative(),
    // --- #293: the win picker's facts (§4.14's Mark won) ---------------------
    /** The number of the quote the order came from, when the reader's QUOTE
     * door answers that quote (§5's quote read rows, D-312 ruling 21), NULL
     * when the order has no quote or the door refuses it. An order carries a
     * quote only when the customer's acceptance of that quote minted it, so a
     * number here names a converted quote the win door accepts by its own
     * id. Optional on the wire only for a server one deploy behind. */
    quote_no: z.string().nullable().optional(),
    /** The deal the order's quote belongs to, only when BOTH the reader's
     * quote door answers that quote and its deal door answers that deal, NULL
     * otherwise (no quote, no link, or either door refusing). The quote door
     * term is ruling 21's: the deal page leaves a quote its reader cannot
     * open out of the deal's linked quotes, so no order row names the link. */
    quote_opportunity_id: Uuid.nullable().optional(),
    /** Whether the order's quote belongs to ANY deal; false for an order with
     * no quote. §7 OMISSION: present only for a holder of
     * `sales.opportunity.write`, and only on a row whose customer that
     * reader's deal write door can hold a deal on (every customer for a wide
     * writer; an assigned customer, or one where the reader owns a deal, for
     * the sales arm). That is where the win door compares the order's
     * customer to a deal the caller writes before it answers 409
     * `QUOTE_LINKED_ELSEWHERE`, and the order door can reach further (a
     * storekeeper's ship-relevant orders). */
    quote_opportunity_linked: z.boolean().optional(),
    /** Whether a won deal names this order as the business that won it. One
     * order backs one deal (D-312 ruling 9), so a win naming it anywhere else
     * answers 409 `ORDER_WON_ELSEWHERE`. §7 OMISSION: served by
     * `quote_opportunity_linked`'s rule, to the reader that 409 already
     * tells. Names no deal. */
    backs_won_opportunity: z.boolean().optional(),
    ...orderImportFields(),
  })
  .openapi("OrderListItem");

export const OrderListResponse = z
  .object({ items: z.array(OrderListItem), next_cursor: z.string().nullable() })
  .openapi("OrderListResponse");

export const OrderLine = z
  .object({
    id: Uuid,
    line_no: z.number().int().min(1),
    quote_line_id: Uuid.nullable(),
    product_id: Uuid,
    description: z.string(),
    qty_ordered: SalesQty,
    qty_shipped: SalesQtyOrZero,
    uom_id: Uuid,
    unit_price_minor: SalesMinorMoneyNonNegative.optional(),
    line_total_minor: SalesMinorMoneyNonNegative.optional(),
    /** §7 OMISSION — `sales.margin.read`. Read it WITH `unit_cost_basis`
     * below, which is what tells the two absences apart. */
    unit_cost_minor: SalesMinorMoneyNonNegative.optional(),
    /**
     * WHICH ABSENCE `unit_cost_minor` IS (added 2026-09-10, D-268 ruling 4).
     *
     * Migration 0055 makes `sales_order_lines.unit_cost_minor` NULL and §3.5's
     * accept transaction writes NULL from an uncosted quote line (KG-3x45 on
     * the demo tenant has no released standard cost). The order line could not
     * say so: the money field is omission-redacted, so a null basis was served
     * exactly like a redaction — telling a `sales.margin.read` holder "you may
     * not see this" about a number that does not exist. §2.14's amendment
     * forbids that collapse in its own words ("absent and null are different
     * facts and must never collapse") and the quote line has always honoured it
     * with a nullable money field.
     *
     * The order line states it with a SECOND KEY rather than by making the
     * money nullable, and the reason is the wire, not taste: `unit_cost_minor`
     * is a PUBLISHED response property, and narrowing a published property to
     * accept null is `response-property-became-nullable` — a breaking change
     * under TESTING §6, which since D-337 needs a reviewed entry in
     * `openapi/breaking-approvals.json`. An ADDED property breaks no client. Three states, no
     * ambiguity: this key ABSENT = §7 redaction; `"unavailable"` (money
     * absent) = no cost basis exists; `"snapshot"` (money present) = the
     * pinned §2.14 value.
     */
    unit_cost_basis: z.enum(["snapshot", "unavailable"]).optional(),
    offcut_class_id: Uuid.nullable(),
    /**
     * THE §2.17 PRICING FACTS (P3-D PR-D1, D-283; migration 0061). The order
     * line carries the same eight facts as its §2.14 quote line — copied at
     * acceptance, written by §3.2 at direct-order creation — because a direct
     * order has no quote line to read them from, and the §3.6 decide, the
     * §4.6 card and the §9.2 degradation record all read THIS row. Seven of
     * the eight are on the wire; `fulfillment_plan` stays off it (the
     * degradation record already serves `planned_mode`).
     *
     * The QuoteLine conventions above apply unchanged: `price_source`,
     * `price_source_ref` and the two BOOLEANS are always present (the
     * unprivileged reader's "needs approval"); `list_price_minor` is §7-omitted
     * without `sales.read_value` and NULL on the manual-price path (no list
     * row priced it); `margin_pct` / `margin_guard_pct` are §7-omitted without
     * `sales.margin.read` and NULL when the cost basis was unavailable —
     * absent and null are different facts and must never collapse.
     *
     * These four REQUIRED properties are ADDED to a published response, which
     * `oasdiff` classes as non-breaking (a client that ignores unknown keys is
     * unaffected); the fallback if that classification ever changes is
     * `.optional()` with the serializer always emitting — wire-identical.
     */
    price_source: PriceSource,
    price_source_ref: Uuid.nullable(),
    margin_breached: z.boolean(),
    off_list: z.boolean(),
    list_price_minor: SalesMinorMoneyNonNegative.nullable().optional(),
    margin_pct: SalesSignedPct.nullable().optional(),
    margin_guard_pct: SalesPct.nullable().optional(),
    fulfillment_mode: FulfillmentMode,
    /** Module 06 reservation ids — the §4.7 detail links each into 06's unit
     * card. Empty for a production line. */
    reservation_ids: z.array(Uuid),
    line_promise_date: SalesBusinessDate.nullable(),
    line_status: OrderLineStatus,
    /** Module 03's promise the line names (P3-J J-2, D-465): the quote line's
     * quoted promise copied at acceptance until planning dates the line, then
     * the committed one the stamp writes; cleared when planning settles the
     * line with no date, and null when the line has none. */
    promise_id: Uuid.nullable(),
    /** The mirror of module 03's promise, written only by the planning
     * consumers (spec 09 §6): `committed` from J-2, the watch's three from
     * J-3. Null while the line has no committed promise. */
    promise_status: z.enum(["committed", "at_risk", "broken", "kept"]).nullable(),
    /** P3-J J-3 (D-466): the first risk module 03's watch found, set while
     * `promise_status` is `at_risk` or `broken` (a broken promise keeps the
     * reason it last carried, null when it broke unflagged). */
    promise_risk_reason: PromiseRiskReasonKey.nullable(),
    /** The date the watch now projects the line's promise to cover, set
     * beside the reason; null while no supply is placed. */
    promise_projected_date: SalesBusinessDate.nullable(),
  })
  .openapi("OrderLine");

/** One entry of §4.7's status timeline. Built from the §2.16 stamps, so it
 * carries only transitions that actually happened — a timeline with future
 * steps drawn in is a timeline that lies about the present. */
export const OrderTimelineEntry = z
  .object({ status: OrderStatus, at: isoDateTime() })
  .openapi("OrderTimelineEntry");

/**
 * One work order pegged to an order line (spec 09 §2.17b, P3-D PR-D6, D-290
 * ruling 2) — a §4.7 WO chip, read from module 09's own mirror, which module
 * 04's transactions write through the peg port, so it is never behind the work
 * order. Operational facts only, never cost or value: every reader of the
 * order sees its chips, whichever facility makes the work order. `openable`
 * says whether THIS reader may open the work order in module 04 (its read
 * fence and the work order's facility), so a chip links only where the link
 * answers.
 */
export const OrderWorkOrder = z
  .object({
    work_order_id: Uuid,
    wo_no: z.string(),
    sales_order_line_id: Uuid,
    facility_id: Uuid,
    status: z.enum(["draft", "released", "in_progress", "completed", "closed", "cancelled"]),
    qty_planned: z.string(),
    qty_good: z.string(),
    uom: z.string(),
    due_date: z.string().nullable(),
    released_at: isoDateTime().nullable(),
    completed_at: isoDateTime().nullable(),
    cancelled_at: isoDateTime().nullable(),
    openable: z.boolean(),
  })
  .openapi("OrderWorkOrder");

/**
 * One §3.6 manual transition (P3-D PR-D4, D-288 ruling 20), from the module's
 * append-only `sales_order_status_overrides` record. The OFFICE detail carries
 * them so §4.7's timeline can print the manager's reason beside the stamp it
 * moved, as it prints `cancelled_reason`; the public tracking page never does.
 */
export const OrderStatusOverride = z
  .object({
    from_status: OrderStatus,
    to_status: OrderStatus,
    /** The manager's words, trimmed of surrounding whitespace, otherwise as typed. */
    reason: z.string(),
    actor_user_id: Uuid,
    at: isoDateTime(),
  })
  .openapi("OrderStatusOverride");

/**
 * `POST /v1/orders/{id}/override-status` (spec 09 §3.6, §5; D-288 rulings
 * 5-8, 17, 18, 21). The target is ANY order status, on purpose: which targets a
 * person may force (ready, shipped, closed — and only on the arcs ruling 5
 * admits) is the server's decision on every press, and a target another door
 * owns is answered 422 STATUS_OVERRIDE_NOT_ALLOWED with `extensions.door`
 * naming that door, rather than a bare 400 from this schema that tells the
 * caller nothing.
 */
export const OrderOverrideStatusRequest = z
  .object({
    target_status: OrderStatus,
    /** Mandatory, like a cancel's: the record of WHY an order skipped the
     * path the system would have walked. */
    reason: z.string().trim().min(1).max(500),
  })
  .strict()
  .openapi("OrderOverrideStatusRequest");

/**
 * THE §2.20 `kind` CHECK VERBATIM, in migration 0066's own order (P3-E PR-E1,
 * D-291). It was a bare string until the notification log's `kind` filter
 * needed the set; the table's CHECK is the only authority for what can appear
 * here, exactly as it is for `status` below. `order_cancelled` is 0066's
 * (D-291 ruling 9: a cancellation of an order the customer was told is
 * confirmed; never for an order that never confirmed, D-283 ruling 6).
 * `payment_reminder` stays absent, as §2.20 says (D-198): module 11 is its
 * only producer and the migration that gives it one adds the value.
 */
export const CustomerNotificationKind = z
  .enum([
    "received",
    "order_confirmed",
    "order_in_production",
    "order_ready",
    "order_shipped",
    "order_cancelled",
    "quote_sent",
    "quote_expiring",
    "ready_not_collected",
    "quote_follow_up",
    // P3-J J-3 (D-469 ruling 2, 0103): the re-promise message.
    "order_re_promised",
  ])
  .openapi("CustomerNotificationKind");

/**
 * THE §2.20 `status` CHECK VERBATIM, in migration 0066's own order with
 * 0074's value appended — this enum serves rows from `customer_notifications`
 * and nothing else, so the table's CHECK is the only authority for what can
 * appear here.
 *
 * D-317 (migration 0074) appends `skipped_customer_archived`: the row's
 * customer was archived (§3.1 item 4) before the dispatcher took it, so
 * nothing was sent and no manager task was opened. A value ADDED to a
 * response enum is not a breaking change under oasdiff (D-286 ruling 24's
 * `partial` precedent), and the office renders every value in words of its own.
 *
 * P3-E PR-E1 (D-291, migration 0066) adds two states with the dispatcher:
 *  * `held` — inside the tenant's quiet hours, awaiting release; the row
 *    carries `send_after` (0066's `customer_notifications_held_has_release`).
 *    Only the factory's own progress and the engine's reminders are ever held
 *    (D-291 ruling 3); the received acknowledgement and the Confirmed push
 *    never are.
 *  * `skipped_superseded` — a held status push collapsed under a newer one of
 *    the same order at release, or a held reminder whose chased state ended
 *    before it went out (D-291 ruling 4). Nothing was sent, on purpose.
 * `queued` is now a state a reader can SEE: the transition writes the row and
 * the dispatcher sends it within seconds (or holds it).
 *
 * It previously read `["queued","sent","delivered","read","failed"]`, which
 * was wrong in BOTH directions and each direction breaks something:
 *  * `skipped_no_channel` / `skipped_opted_out` were MISSING, and they are
 *    states the quote slice writes — §3.4's send and §3.6's reminder record a
 *    skip when the customer has no messaging channel or has opted out
 *    (`QuoteRemindResponse.message_id` is nullable for exactly that case). A
 *    skipped row would therefore fail validation on its way out of
 *    `OrderDetailResponse.notifications`, so the §4.7 panel breaks on the
 *    first customer without Telegram — the common case at onboarding.
 *  * `read` was PRESENT with no producer: the CHECK forbids it, so no writer
 *    can ever store it. Read receipts are module 17's delivery state on
 *    `outbound_messages`, reachable through `message_id`; naming one here
 *    promised a fact this table does not hold.
 */
export const CustomerNotificationStatus = z
  .enum([
    "queued",
    "held",
    "sent",
    "delivered",
    "failed",
    "skipped_no_channel",
    "skipped_opted_out",
    "skipped_superseded",
    "skipped_customer_archived",
  ])
  .openapi("CustomerNotificationStatus");

/**
 * §2.20's row, as the §4.7 notifications panel, the quote composer's
 * Notifications card and `GET /v1/customer-notifications` read it. One shape
 * for all three readers (P3-E PR-E1, D-291).
 */
export const CustomerNotification = z
  .object({
    id: Uuid,
    kind: CustomerNotificationKind,
    /** The channel module 17 dispatched on once the row was handed off; while
     * `queued` or `held`, the channel the customer's §2.1 preference points
     * at (the dispatcher rewrites it to the one it resolved). */
    channel: z.enum(["telegram", "whatsapp", "email"]),
    status: CustomerNotificationStatus,
    /** The document the message is about — at least one is set (§2.20's
     * `customer_notifications_has_subject`). The log read spans documents,
     * so a row names its own. */
    sales_order_id: Uuid.nullable(),
    quote_id: Uuid.nullable(),
    /** The module-17 message this rode; NULL until the dispatcher hands the
     * row off, and for every skipped row. */
    message_id: Uuid.nullable(),
    /**
     * A `held` row's release instant as it was computed when the row was
     * held: §4.7's "Held until". The CURRENT quiet-hours settings decide the
     * actual release (D-291 ruling 3), so a window shortened afterwards
     * releases the row earlier than this. On a `queued` row it is the retry
     * backoff after a dispatcher-side failure (D-291 ruling 6). NULL otherwise.
     */
    send_after: isoDateTime().nullable(),
    /**
     * A stable UPPER_SNAKE code, never server prose (D-225): the surface
     * localizes it. Present exactly when `status` is `failed` (0058's
     * biconditional CHECK) — e.g. `TEMPLATE_NOT_FOUND`, or `SEND_ERROR` after
     * the dispatcher's fifth failed attempt (D-291 ruling 6).
     */
    failed_reason: z.string().nullable(),
    created_at: isoDateTime(),
    updated_at: isoDateTime(),
  })
  .openapi("CustomerNotification");

/** D-242's order enum for `GET /v1/customer-notifications`. `-created_at` is
 * the default: the log's question is "what did we just tell the customer".
 * `kind` and `status` are raw enums under a translated chip, so they are
 * filters, never sort keys (the `QuotesOrder` rule). */
export const CustomerNotificationsOrder = z
  .enum(["-created_at", "created_at"])
  .openapi("CustomerNotificationsOrder");

/**
 * `GET /v1/customer-notifications` query (spec 09 §5, P3-E PR-E1, D-291
 * ruling 11). Every filter is optional and they AND together; a value outside
 * its enum, or an id that is not a uuid, is 400 VALIDATION_FAILED rather than
 * an empty page. The controller parses with THIS schema, so the contract and
 * the door cannot disagree about what a filter admits.
 */
export const CustomerNotificationListQuery = z.object({
  sales_order_id: z.guid().optional(),
  quote_id: z.guid().optional(),
  customer_id: z.guid().optional(),
  kind: CustomerNotificationKind.optional(),
  status: CustomerNotificationStatus.optional(),
  order: CustomerNotificationsOrder.optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
});

export const CustomerNotificationListResponse = z
  .object({ items: z.array(CustomerNotification), next_cursor: z.string().nullable() })
  .openapi("CustomerNotificationListResponse");

// ---------------------------------------------------------------------------
// §2.18 / §2.19 SHIPMENTS — the pick, pack, ship and delivered surface (P3-D
// PR-D3, D-286; migration 0063). Deferred by D-258 through PR-D2; landed here.
//
// NO MONEY ANYWHERE IN THIS BLOCK, and not by omission-per-role: §7's
// redaction row 2 says the pick screen shows QTY ONLY, and the storekeeper —
// the reader every one of these shapes exists for — holds no `sales.read_value`.
// A field that does not exist cannot be leaked by a serializer change (the
// `PublicOrderLine` argument, applied to the floor). The order-line PRICE a
// shipment carries rides `sales.shipment_shipped` (spec 09 §6 — the invoice
// contract for module 11) and never a response here.
//
// QUANTITY FRAMES (risk 3 of the build plan): `qty_picked` is the ORDER
// LINE's `numeric(14,3)` (`SalesQty`, the same frame `qty_ordered` /
// `qty_shipped` keep); a unit's `remaining_measure` is module 06's
// `numeric(18,6)` (`SalesMeasure`, D-265's lesson) in the unit's own measure
// UoM. The two never share a schema, so a "1600.000000" can never be refused
// as a sales quantity on its way back in.
// ---------------------------------------------------------------------------

/** One §2.19 row — a physical pick, WITH its module-06 movement (ledger-first:
 * `inventory_movement_id` is never null). `undone_at` set = the pick was
 * reversed (`DELETE /v1/shipments/{id}/lines/{lineId}` is a soft undo, the
 * mirror `reversal` movement posted in the same transaction); an undone row
 * stays on the wire as history and counts for nothing. `stock_unit_id` is the
 * PARENT unit — no child is minted and no cut performed (D-208 pt 5): the
 * metres leave the same container and the remainder stays on it. */
export const ShipmentLine = z
  .object({
    id: Uuid,
    sales_order_line_id: Uuid,
    line_no: z.number().int().min(1),
    stock_unit_id: Uuid,
    serial_code: z.string(),
    lot_id: Uuid,
    lot_code: z.string(),
    /** In the ORDER LINE's UoM (`numeric(14,3)`). */
    qty_picked: SalesQty,
    uom_code: z.string(),
    /** true when `qty_picked` < the unit's remaining at pick time — the LINE's
     * fact. `stock_units.is_offcut` is never written by a shipment (spec 06
     * §2.3: set only when SPAWNED — D-286 ruling 12). */
    is_cut: z.boolean(),
    inventory_movement_id: Uuid,
    scanned_at: isoDateTime(),
    undone_at: isoDateTime().nullable(),
  })
  .openapi("ShipmentLine");

/** A unit the pick plan names for a line — the line's OWN active hold(s) as
 * module 06 holds them, read inside the shipment's own transaction. The floor
 * renders "Bobbin E2E-… · WH-C/A3 · 1600 m" from it (§4.8). */
export const PickPlanUnit = z
  .object({
    stock_unit_id: Uuid,
    serial_code: z.string(),
    location_code: z.string(),
    /** Module 06's `numeric(18,6)` measure in `measure_uom` — NOT a sales
     * quantity (D-265). The client shows the remainder preview from it; the
     * server's 422 QTY_EXCEEDS_REMAINING is the truth. */
    remaining_measure: SalesMeasure,
    measure_uom: z.string(),
    /** The hold's quantity in module 06's BASE frame (6 dp). */
    hold_qty: SalesMeasure,
  })
  .openapi("PickPlanUnit");

/** One order line as the pick screen sees it: what is ordered, what has
 * already shipped on earlier shipments, what is picked on THIS shipment
 * (live rows only), and the open remainder — all in the line's `numeric(14,3)`
 * frame, so the client never converts. `line_status` is the order line's
 * mirror (§2.17); a line that is not `open` / `ready` cannot be picked. */
export const PickPlanLine = z
  .object({
    sales_order_line_id: Uuid,
    line_no: z.number().int().min(1),
    description: z.string(),
    product_id: Uuid,
    qty_ordered: SalesQty,
    qty_shipped: SalesQtyOrZero,
    qty_picked_here: SalesQtyOrZero,
    /** `qty_ordered − qty_shipped − qty_picked_here`, server-computed. */
    qty_open: SalesQtyOrZero,
    uom_code: z.string(),
    line_status: OrderLineStatus,
    reserved_units: z.array(PickPlanUnit),
  })
  .openapi("PickPlanLine");

/** The §4.8 pick screen's ONE read and every shipment door's response. */
export const ShipmentDetailResponse = z
  .object({
    id: Uuid,
    shipment_no: z.string(),
    order_id: Uuid,
    order_no: z.string(),
    customer_display_name: z.string(),
    facility_id: Uuid,
    status: ShipmentStatus,
    carrier: z.string().nullable(),
    tracking_ref: z.string().nullable(),
    /** The delivery note rendered ONCE at pack and frozen (D-288 rulings 10,
     * 11). NULL while `picking`, and NULL on a packed shipment whose note
     * could not be drawn — the pack stood, and `POST /v1/shipments/{id}/
     * delivery-note` re-renders it. Read through
     * `GET /v1/shipments/{id}/delivery-note.pdf`. */
    delivery_note_file_id: Uuid.nullable(),
    picked_by: Uuid.nullable(),
    shipped_at: isoDateTime().nullable(),
    delivered_at: isoDateTime().nullable(),
    delivered_by: DeliveredBy.nullable(),
    cancelled_at: isoDateTime().nullable(),
    /** Every §2.19 row of the shipment, undone ones included (history). */
    lines: z.array(ShipmentLine),
    /** The order's non-cancelled lines with their open remainder and the
     * module-06 holds that plan them — composed through two bounded in-txn
     * reads of module 06, one call each whatever the line count. */
    pick_plan: z.array(PickPlanLine),
  })
  .openapi("ShipmentDetailResponse");

/** The row the §4.7 shipments panel and `GET /v1/shipments` render. */
export const ShipmentSummary = z
  .object({
    id: Uuid,
    shipment_no: z.string(),
    order_id: Uuid,
    order_no: z.string(),
    facility_id: Uuid,
    status: ShipmentStatus,
    /** LIVE lines (undone rows excluded). */
    line_count: z.number().int().nonnegative(),
    /** The frozen delivery note, when one was drawn (D-288) — the §4.7
     * shipments panel links it, or offers the re-render when it is NULL on a
     * packed-or-later shipment. */
    delivery_note_file_id: Uuid.nullable(),
    carrier: z.string().nullable(),
    tracking_ref: z.string().nullable(),
    shipped_at: isoDateTime().nullable(),
    delivered_at: isoDateTime().nullable(),
    delivered_by: DeliveredBy.nullable(),
    created_at: isoDateTime(),
  })
  .openapi("ShipmentSummary");

export const ShipmentListResponse = z
  .object({ items: z.array(ShipmentSummary), next_cursor: z.string().nullable() })
  .openapi("ShipmentListResponse");

/** `POST /v1/orders/{id}/shipments`. v1 ships from the ORDER's fulfilling
 * facility, where the holds are by construction; a supplied `facility_id`
 * that differs answers 422 FACILITY_NOT_ELIGIBLE. Optional so the floor,
 * which knows only the order, sends `{}`. */
export const OpenShipmentRequest = z
  .object({ facility_id: Uuid.optional() })
  .strict()
  .openapi("OpenShipmentRequest");

/** `POST /v1/shipments/{id}/lines` — one scan. `stock_unit_id` comes off the
 * QR resolve (`POST /v1/inventory/scan`) or a typed serial; `qty_picked` is
 * in the ORDER LINE's UoM. `override: true` is the §7 "mis-scan reservation
 * override" and is honoured ONLY for a facility_admin / group_admin lineage
 * AND ONLY on a unit reserved for nothing (D-286 ruling 7): a unit held for
 * ANOTHER demand is refused whatever the flag. */
export const PickRequest = z
  .object({
    stock_unit_id: Uuid,
    sales_order_line_id: Uuid,
    qty_picked: SalesQty,
    override: z.boolean().optional(),
  })
  .strict()
  .openapi("PickRequest");

/** `POST /v1/shipments/{id}/ship`. Both free text in v1 (§2.18: `self_pickup`,
 * `own_truck`, a carrier name); their first and only writer. */
export const ShipRequest = z
  .object({
    carrier: z.string().min(1).max(120).optional(),
    tracking_ref: z.string().min(1).max(120).optional(),
  })
  .strict()
  .openapi("ShipRequest");

export const OrderDetailResponse = z
  .object({
    id: Uuid,
    order_no: z.string(),
    customer_id: Uuid,
    customer_display_name: z.string(),
    contact_id: Uuid.nullable(),
    owner_user_id: Uuid,
    quote_id: Uuid.nullable(),
    /** The number of the quote the order came from, when the reader's QUOTE
     * door answers that quote (D-312 ruling 21, #293), NULL when the order has
     * no quote or the door refuses it, the order list row's rule. */
    quote_no: z.string().nullable(),
    fulfilling_facility_id: Uuid,
    status: OrderStatus,
    credit_check_status: CreditCheckStatus,
    /** How much the order exceeded the limit by, in minor units — the §4.6
     * credit card's headline. Present only on a `blocked_credit` order for a
     * reader holding `sales.read_value`. */
    credit_over_limit_minor: SalesMinorMoneyNonNegative.optional(),
    currency: SalesCurrency,
    subtotal_minor: SalesMinorMoneyNonNegative.optional(),
    tax_minor: SalesMinorMoneyNonNegative.optional(),
    total_minor: SalesMinorMoneyNonNegative.optional(),
    promise_date: SalesBusinessDate.nullable(),
    /** §4.7's at-risk flag and its reason, the list's rule (P3-J J-3). */
    at_risk: z.boolean(),
    at_risk_reason: OrderAtRiskReason.nullable(),
    customer_po_ref: z.string().nullable(),
    ...orderImportFields(),
    /** The §4.10 tracking deep link's key — the office copies it to the
     * customer. Public and enumeration-resistant by uuid v7 + rate limit.
     * ABSENT without `sales.read_value` (D-337 ruling 2), whatever the
     * tenant's `tracking_show_prices` says: the token outlives that setting
     * and carries the customer's confirm-received write. */
    public_token: Uuid.optional(),
    cancelled_reason: z.string().nullable(),
    confirmed_at: isoDateTime().nullable(),
    /** P3-J J-2 (spec 09 §3.6, D-465 ruling 3): set while the Confirmed
     * message waits for the order's promise dates, the instant the deadline
     * pass sends it undated; null once it is written or when it never waited.
     * A credit release sets it too when the release leaves a line awaiting a
     * promise, and then it marks the dated notice the order is owed (D-644).
     * The detail reads "Sends with the delivery date" while it is set. */
    confirmed_notice_due_at: isoDateTime().nullable(),
    created_at: isoDateTime(),
    updated_at: isoDateTime(),
    timeline: z.array(OrderTimelineEntry),
    /** §3.6's manual transitions, oldest first (D-288). Empty on an order
     * that only ever moved through its own doors. */
    status_overrides: z.array(OrderStatusOverride),
    /** Whether the CALLER's write scope covers this order's fulfilling
     * facility (D-286 ruling 25's rule, as a fact on the read — D-118): a
     * facility-scoped admin may read another facility's order, and every
     * facility-fenced door (cancel, line cancel, mark delivered, force status,
     * the shipment open door, the delivery-note re-render) answers them 403, so
     * a client offers none of those controls while this is false. Group-scoped
     * callers always read true. */
    facility_writable: z.boolean(),
    /** Whether `POST /v1/orders/{id}/requote` for this order's OWN customer
     * would pass the target-customer checks for THIS caller (P3-D PR-D5,
     * D-289, found in review — the facility_writable pattern, D-118): the
     * customer is not archived and, for a sales-narrowed caller, is assigned
     * to them. A sales rep can read an order they OWN after being unassigned
     * from its customer, and an archived customer's orders stay readable;
     * without this flag the Requote press answered 404 CUSTOMER_NOT_FOUND or
     * 422 CUSTOMER_ARCHIVED every time. It says nothing about the caller's
     * permission to write quotes, which the client reads from the role mirror. */
    requotable: z.boolean(),
    lines: z.array(OrderLine),
    notifications: z.array(CustomerNotification),
    /** §3.5a's degradation record — the lines whose named stock vanished
     * between send and accept. Empty on a clean order. */
    fulfillment_degradations: z.array(
      z
        .object({
          line_no: z.number().int().min(1),
          planned_mode: FulfillmentMode,
          actual_mode: FulfillmentMode,
          /** The discount delta the degradation cost (§9.2's money impact).
           * §7-omitted without `sales.read_value`. */
          impact_minor: SalesMinorMoney.optional(),
          occurred_at: isoDateTime(),
        })
        .strict(),
    ),
    /** The §3.6 review record of a DIRECT order (P3-D PR-D1, D-283) — served
     * ONLY while the order is `pending_approval` and OMITTED otherwise, the
     * D-255 shape `QuoteDetailResponse.review` already has: §2.16 carries no
     * decision columns, so a decision lives in `sales.order_review_decided`
     * and the audit chain, and an object of nulls after the fact would be a
     * record that exists and knows nothing. `required_approver` is the §3.4
     * tier re-derived from the order line's STORED breach facts (never by
     * repricing — §9.3), so the detail can name the desk the order waits for
     * and the client never re-derives the threshold. The four decision fields
     * are null for the whole of the record's life on the wire: "a review is
     * open" is the only state this object is ever served in. */
    review: z
      .object({
        required_approver: RequiredApprover,
        outcome: ReviewOutcome.nullable(),
        decided_by: Uuid.nullable(),
        decided_at: isoDateTime().nullable(),
        note: z.string().nullable(),
      })
      .optional(),
    /**
     * §4.7's shipments panel (P3-D PR-D3, D-286) — every shipment of the order
     * THIS READER MAY SEE, newest first. Both shipment tables carry the D-074
     * RESTRICTIVE facility fence (migration 0063), so a facility-scoped reader
     * outside the order's fulfilling facility reads FEWER rows than the order
     * has; the screen must not present an empty array as "never shipped" for
     * such a reader (the storekeeper of the fulfilling facility, the reader the
     * panel exists for on the floor, is inside the fence by construction). A
     * REQUIRED array ADDED to a published response — non-breaking under
     * `oasdiff`, and every typed fixture carries `shipments: []`.
     */
    shipments: z.array(ShipmentSummary),
    /** §4.7's WO chips (P3-D PR-D6, D-290): every work order pegged to one of
     * this order's lines, oldest first, from module 09's mirror. A line is
     * `in_production` while at least one of its work orders is released or in
     * progress. A REQUIRED array ADDED to a published response — non-breaking
     * under `oasdiff`; every typed fixture carries `work_orders: []`. */
    work_orders: z.array(OrderWorkOrder),
  })
  .openapi("OrderDetailResponse");

export const OrderCancelRequest = z
  .object({ reason: z.string().min(1).max(2000) })
  .strict()
  .openapi("OrderCancelRequest");

/** `POST /v1/orders/{id}/re-promise` (P3-J J-3, D-469 ruling 1): a quoted
 * promise from row 74 for one of the order's lines. The customer hears of the
 * new date only when `notify_customer` is true (ruling 2). */
export const OrderRePromiseRequest = z
  .object({
    sales_order_line_id: Uuid,
    promise_id: Uuid,
    notify_customer: z.boolean().default(false),
  })
  .strict()
  .openapi("OrderRePromiseRequest");

// ---------------------------------------------------------------------------
// §5 `POST /v1/orders` + `POST /v1/orders/{id}/decide` — the DIRECT ORDER
// (P3-D PR-D1, D-283). Deferred by D-258 through P3-C; landed here.
// ---------------------------------------------------------------------------

/** A direct order's line: the composer row plus an optional promise date the
 * seller typed (D-349 ruling 1). The date is on or after the tenant's
 * business today, else 422 PROMISE_DATE_IN_PAST, and becomes the line's
 * `line_promise_date`. A quote line has none, because P3-J's CTP dates it. */
export const OrderLineInput = z
  // `promise_id` is a quote line's (P3-J J-2): a direct order's line is dated
  // by its typed date or by the confirmation drain, never by a quoted promise.
  .object({
    ...QuoteLineInput.omit({ promise_id: true }).shape,
    promise_date: SalesBusinessDate.nullish(),
  })
  .strict()
  .openapi("OrderLineInput");

/** The direct order's write shape. The LINE shape is `QuoteLineInput`
 * extended by one field, not copied — the same §4.4 composer row feeds both
 * documents, and
 * the same rule applies: `unit_price_minor` ABSENT means "resolve it" (§3.2),
 * PRESENT means a manual price that sets `off_list` and can trip the §2.11
 * guard; `fulfillment_plan` is the §3.5a plan, reserved on entry to
 * `confirmed`. Pricing is at the tenant's business day (§2.16 carries no
 * requested date), the header `promise_date` is the latest typed line date or
 * NULL (spec 09 §2.16), the currency is the
 * customer's `default_currency` (never a body field, §9.13), and the owner is
 * the customer's §2.2 owner else the creator.
 *
 * `fulfilling_facility_id` is OPTIONAL: omitted = the §2.16 derivation over
 * the caller's scope-filtered ACTIVE facilities; an inactive facility answers
 * 422 FACILITY_NOT_ELIGIBLE, an active one outside a facility-scoped caller's
 * grants 403, and no eligible facility at all 422 FACILITY_UNRESOLVED.
 *
 * There is deliberately NO `internal_note`: `sales_orders` has no such column
 * (0055 — the only `internal_note` in the module is the quote revision's),
 * and a field the row cannot keep would be accepted and silently dropped. */
export const OrderCreateRequest = z
  .object({
    customer_id: Uuid,
    contact_id: Uuid.nullish(),
    customer_po_ref: z.string().max(200).nullish(),
    fulfilling_facility_id: Uuid.nullish(),
    lines: z.array(OrderLineInput).min(1).max(200),
  })
  .strict()
  .openapi("OrderCreateRequest");

/** The §3.6 decision on a `pending_approval` direct order — the
 * `QuoteDecideRequest` shape, and the same rule about the note: mandatory on
 * reject, enforced in the service (422 DECISION_NOTE_REQUIRED), because a
 * conditional requirement is not expressible as one zod object without
 * turning the DTO into a union the client would have to discriminate. */
export const OrderDecideRequest = z
  .object({
    outcome: ReviewOutcome,
    note: z.string().max(2000).nullish(),
  })
  .strict()
  .openapi("OrderDecideRequest");

/** §2.16's credit-release grammar (D-231). `prepayment_recorded` is the
 * module-11 stand-in: the producer of `finance.prepayment_received` does not
 * exist until P4, so the decision endpoint records the out-of-band payment
 * and the transition is audited exactly as the event-driven one will be. */
export const CreditDecisionRequest = z
  .object({
    outcome: z.enum(["approved", "prepayment_recorded", "upheld"]),
    note: z.string().max(2000).nullish(),
  })
  .strict()
  .openapi("CreditDecisionRequest");

export const CreditDecisionResponse = z
  .object({
    order_id: Uuid,
    credit_check_status: CreditCheckStatus,
    released_via: z.enum(["approval", "prepayment"]).nullable(),
  })
  .openapi("CreditDecisionResponse");

// ---------------------------------------------------------------------------
// §4.6 review inbox — the one read behind the decider's screen
// ---------------------------------------------------------------------------

/**
 * §4.6's four card kinds, as ONE typed feed rather than four list reads the
 * screen would have to interleave by age itself.
 *
 * `fulfillment_degradation` carries NO decide action by design (§4.6: "render
 * informational" — the order already exists and the card IS the visibility).
 * Declaring it in the same union as the decidable kinds is what makes that a
 * property of the data rather than a rule a screen has to remember.
 */
export const ReviewCardKind = z
  .enum([
    "quote_margin_breach",
    "order_margin_breach",
    "price_override",
    "credit_block",
    "fulfillment_degradation",
  ])
  .openapi("ReviewCardKind");

export const ReviewCard = z
  .object({
    kind: ReviewCardKind,
    /** The subject document. `quote_id`/`order_id`/`override_id` — exactly
     * one is non-null, matching `kind`. */
    quote_id: Uuid.nullable(),
    order_id: Uuid.nullable(),
    override_id: Uuid.nullable(),
    /** What the deep link says on it (`Q-2026-00042`, `SO-2026-00087`). */
    document_no: z.string(),
    customer_id: Uuid,
    customer_display_name: z.string(),
    /** §4.6 "one row per item, AMOUNT, requester, age". §7-omitted without
     * `sales.read_value`. */
    amount_minor: SalesMinorMoney.optional(),
    currency: SalesCurrency,
    requested_by: Uuid,
    requested_at: isoDateTime(),
    /** The breaching lines, for the card's "line, margin vs guard, impact"
     * summary. Empty for credit and degradation cards. */
    breach_lines: z.array(
      z
        .object({
          line_no: z.number().int().min(1),
          product_id: Uuid,
          description: z.string(),
          margin_pct: SalesSignedPct.optional(),
          guard_pct: SalesPct.optional(),
          delta_minor: SalesMinorMoney.optional(),
          off_list: z.boolean(),
        })
        .strict(),
    ),
    /** Credit cards only: how far over the limit (§10 M09-S10's headline). */
    over_limit_minor: SalesMinorMoneyNonNegative.optional(),
    credit_check_status: CreditCheckStatus.nullable(),
    /** True when THIS reader's grant clears the §3.4 threshold for this card
     * (breach ≤2pp → facility_admin; >2pp or any breached line above the cap
     * → group_admin only). The server decides the threshold; the screen shows
     * the copy either way and never offers a button that will 403. */
    decidable_by_caller: z.boolean(),
  })
  .openapi("ReviewCard");

export const ReviewCardListResponse = z
  .object({ items: z.array(ReviewCard), next_cursor: z.string().nullable() })
  .openapi("ReviewCardListResponse");

// ---------------------------------------------------------------------------
// §4.10 public pages — token-resolved, unauthenticated, cost-free BY SCHEMA
// ---------------------------------------------------------------------------

/** A public quote line. NO cost, NO margin, NO internal note — not omitted,
 * ABSENT (§4.10: "no cost/margin ever"). */
export const PublicQuoteLine = z
  .object({
    line_no: z.number().int().min(1),
    description: z.string(),
    qty: SalesQty,
    uom_code: z.string(),
    unit_price_minor: SalesMinorMoneyNonNegative,
    line_total_minor: SalesMinorMoneyNonNegative,
    /** The line's checked delivery date (P3-J J-2, D-465 ruling 5): the page
     * prints "Delivery by {date}, confirmed when you accept"; null prints
     * nothing about a date. */
    delivery_by: SalesBusinessDate.nullable(),
  })
  .strict()
  .openapi("PublicQuoteLine");

/**
 * §4.10's states as a discriminated union — the whole point of this shape. The
 * page renders one designed screen per state; a single optional-riddled
 * payload would let a client render the valid page for an expired quote by
 * forgetting a check.
 *
 * FOUR MEMBERS, NOT FIVE. §4.10 names five states and the fifth — not-found —
 * is deliberately NOT a member here: an unresolvable token is a 404 carrying
 * the ordinary RFC 9457 problem (`code: TOKEN_INVALID`), because a 200 whose
 * body says "this does not exist" is wrong HTTP, poisons caches and makes the
 * door invisible to every monitor that counts 4xx. The CLIENT normalises that
 * 404 into its own fifth rendering state so its switch stays exhaustive — the
 * generic 404 page, with no tenant name and no branding, is what §4.10 is
 * actually asking for and it is a screen, not a payload.
 *
 * The three non-valid members DO resolve, and they carry the seller block on
 * purpose: "your offer expired, here is your manager" is the entire value of
 * the expired page, and a customer holding a real (if stale) token has already
 * been told which company sent it.
 */
export const PublicQuoteSeller = z
  .object({
    /** The tenant's own display name — safe to render ONLY after the token
     * resolved, which is why it hangs off the resolved states and not the
     * envelope. */
    display_name: z.string(),
    /** The responsible manager, for "ask your manager" (§4.10 expired). */
    manager_name: z.string().nullable(),
    /** A deep link the customer can press (`https://t.me/…`, `mailto:`).
     * NULL when the manager has no publishable channel. */
    manager_contact_url: z.url().nullable(),
  })
  .strict()
  .openapi("PublicQuoteSeller");

export const PublicQuoteResponse = z
  .discriminatedUnion("state", [
    z
      .object({
        state: z.literal("valid"),
        quote_no: z.string(),
        /** §4.10: the page's language comes from the QUOTE/customer locale,
         * never the browser's — the customer chose it once, on the record. */
        locale: PublicPageLocale,
        customer_display_name: z.string(),
        seller: PublicQuoteSeller,
        currency: SalesCurrency,
        vat_mode: z.enum(["inclusive", "exclusive"]),
        vat_rate_pct: SalesPct,
        subtotal_minor: SalesMinorMoneyNonNegative,
        tax_minor: SalesMinorMoneyNonNegative,
        total_minor: SalesMinorMoneyNonNegative,
        note_to_customer: z.string().nullable(),
        valid_until: SalesBusinessDate,
        /** The countdown's anchor — server time, so a wrong device clock
         * cannot make an expired quote look live. */
        server_time: isoDateTime(),
        lines: z.array(PublicQuoteLine),
      })
      .strict(),
    z
      .object({
        state: z.literal("expired"),
        quote_no: z.string(),
        locale: PublicPageLocale,
        seller: PublicQuoteSeller,
        /** The END OF THE VALIDITY WINDOW — always a real date, and only
         * PRINTABLE when `withdrawn` is false (see below). */
        expired_on: SalesBusinessDate,
        /**
         * TRUE when the seller withdrew this offer BEFORE its window closed
         * (added 2026-09-10, D-268 ruling 3).
         *
         * §4.10's expired page says "It was valid until {{date}}". That is true
         * of one of the two ways a quote reaches this state — the 02:00 cron,
         * which expires it BECAUSE `valid_until` passed. It is false of the
         * other: `POST /v1/quotes/{id}/expire` (§4.5's bulk force-expire)
         * withdraws an offer by hand and only flips the status, so the page told
         * a customer, in their own language, that their dead offer was good
         * until a day up to FOURTEEN DAYS IN THE FUTURE.
         *
         * The fact is derivable and needs no column: `valid_until` strictly
         * before the tenant's HQ calendar day (§9.14 — never UTC, or a
         * withdrawal made on the last evening reads as a lapse). It is a
         * SECOND KEY rather than a nullable `expired_on` because that property
         * is already published, and narrowing it to accept null is
         * `response-property-became-nullable` — breaking under TESTING §6, with
         * no approval bridge wired. An added property breaks no client, and
         * `expired_on` stays what it always was: the end of the window.
         */
        withdrawn: z.boolean(),
      })
      .strict(),
    z
      .object({
        state: z.literal("superseded"),
        quote_no: z.string(),
        locale: PublicPageLocale,
        seller: PublicQuoteSeller,
        /** THERE IS NO `current_path`, and §4.10's "auto-forward to the newest
         * revision" is AMENDED in this PR because it cannot be built without
         * giving up the property that makes the door safe. `token_hash` stores
         * sha256 of the customer's credential and the raw token is never
         * written down, so nothing in the database can reconstruct the newer
         * link. Forwarding would mean storing the raw token — publishing every
         * live acceptance credential into the database, its backups and its
         * query logs to save one hop the customer can make by opening the
         * message that carried the new offer.
         *
         * What the page says instead is the honest and more useful sentence:
         * a newer offer was sent, here is who to ask — which is what `seller`
         * is on every resolved state for. `sent_at` dates it so the customer
         * can tell whether the newer message is one they have already seen. */
        superseded_at: isoDateTime(),
      })
      .strict(),
    z
      .object({
        state: z.literal("accepted"),
        quote_no: z.string(),
        locale: PublicPageLocale,
        seller: PublicQuoteSeller,
        order_no: z.string(),
        /** Same-origin path to §4.10's tracking page. */
        tracking_path: z.string(),
        accepted_at: isoDateTime(),
      })
      .strict(),
  ])
  .openapi("PublicQuoteResponse");

export const PublicQuoteAcceptResponse = z
  .object({
    order_no: z.string(),
    tracking_path: z.string(),
    /** True when §3.5a had to degrade a line (the customer sees no error —
     * §10 M09-S06 — but the payload records it for the confirmation copy the
     * seller reads back). */
    degraded: z.boolean(),
  })
  .openapi("PublicQuoteAcceptResponse");

export const PublicQuoteDeclineRequest = z
  .object({
    reason_code: QuoteRejectionReason.optional(),
    note: z.string().max(2000).nullish(),
  })
  .strict()
  .openapi("PublicQuoteDeclineRequest");

export const PublicQuoteDeclineResponse = z
  .object({ declined: z.literal(true) })
  .openapi("PublicQuoteDeclineResponse");

/** §4.10 tracking. Prices are ABSENT unless `sales_settings.tracking_show_prices`
 * — and when the tenant has not opted in the keys are not on the payload at
 * all, which is the same omission rule §7 uses for roles, applied to a tenant
 * setting. */
export const PublicOrderLine = z
  .object({
    line_no: z.number().int().min(1),
    description: z.string(),
    qty_ordered: SalesQty,
    qty_shipped: SalesQtyOrZero,
    uom_code: z.string(),
    unit_price_minor: SalesMinorMoneyNonNegative.optional(),
    line_total_minor: SalesMinorMoneyNonNegative.optional(),
  })
  .strict()
  .openapi("PublicOrderLine");

/**
 * One SHIPPED shipment of the order as the §4.10 tracking page lists it (P3-E
 * PR-E2, D-292 ruling 12 — closing D-286 ruling 15's stated pending). Only
 * shipments that have left the building appear (`shipped` or `delivered`),
 * ordered by `shipped_at`. NO MONEY and NO OVERRIDE REASON, by the schema and
 * not by a redaction: the customer reads what was sent, when, with whom, and
 * how its arrival was confirmed. `delivered_by: auto` is the engine's
 * auto-confirm after `delivery_auto_confirm_days` (D-292 ruling 10), which the
 * page labels as such.
 */
export const PublicOrderShipment = z
  .object({
    shipment_no: z.string(),
    shipped_at: isoDateTime(),
    carrier: z.string().nullable(),
    tracking_ref: z.string().nullable(),
    delivered_at: isoDateTime().nullable(),
    delivered_by: DeliveredBy.nullable(),
  })
  .strict()
  .openapi("PublicOrderShipment");

/** Same rule as the quote door: an unresolvable tracking token is a 404 with
 * the ordinary problem document, never a 200 saying "nothing here". The union
 * stays a union of ONE for now so the P3-D shipment states join it as members
 * rather than as a shape change. */
export const PublicOrderResponse = z
  .discriminatedUnion("state", [
    z
      .object({
        state: z.literal("ok"),
        order_no: z.string(),
        locale: PublicPageLocale,
        seller: PublicQuoteSeller,
        status: OrderStatus,
        timeline: z.array(OrderTimelineEntry),
        promise_date: SalesBusinessDate.nullable(),
        currency: SalesCurrency,
        total_minor: SalesMinorMoneyNonNegative.optional(),
        lines: z.array(PublicOrderLine),
        /** §4.10's "confirm received" button — the server decides whether it
         * is offered, so the page never shows a button whose press would
         * 409. SERVER-COMPUTED as `status === 'shipped'` since 2026-09-17
         * (P3-D PR-D3, D-286): `POST …/confirm-received` flips every shipped
         * shipment and the order to `delivered` (the customer's press is the
         * source of truth, `delivered_by: customer`), answers this same
         * payload with the flag back to false, and answers the IDENTICAL 200
         * on a replay while the order is already `delivered` — a customer
         * saying "I have it" twice is one fact. */
        can_confirm_received: z.boolean(),
        /** The order's shipped shipments, oldest first (P3-E PR-E2, D-292
         * ruling 12); empty until the first one ships. */
        shipments: z.array(PublicOrderShipment),
      })
      .strict(),
  ])
  .openapi("PublicOrderResponse");

// ---------------------------------------------------------------------------
// problem codes
// ---------------------------------------------------------------------------
//
// THIS SLICE ADDS NO SECOND ARRAY. Every code the quote core raises lives in
// `salesProblemCodes` (./schemas.ts) beside the P3-A ones, because the
// module-catalog parity gate holds THAT array against every code
// apps/api/src/modules/sales raises — both directions. A per-slice array would
// be a second place a code could hide from the gate, which is the exact defect
// the gate exists to catch.

// ---------------------------------------------------------------------------
// §2.21 / §4.9 SALES ANALYTICS — the daily rollup's read (P3-E PR-E3, D-293;
// migration 0068).
//
// ONE ROW PER (day, manager, tier, currency), exactly §2.21's unique. Currency
// is a KEY, never a sum (ruling 4): a client that wants a tenant total sums
// WITHIN a currency and renders one figure per currency, and there is no field
// here that could hold a mixed-currency number.
//
// WHAT IS NOT HERE, and why. §4.9 lists eight elements; two of them are not
// this table's and stay live reads on the screen (ruling 3): AVG RESPONSE TIME
// is module 17's inbound/first-send timestamps, and the OFF-CUT MONETIZATION
// tile needs module 06's off-cut stock value as its denominator. Copying
// another module's facts into module 09's rollup is the cross-module table
// read the constitution forbids, so the screen is a hybrid by construction and
// says so where a number is missing rather than showing a wrong one.
//
// WIN RATE IS NOT A COLUMN EITHER: §3.10 computes it at query time over a
// selectable window (accepted / sent), so a client that wants a different
// window does not need a different rollup. The counts it divides are here.
// ---------------------------------------------------------------------------

export const SalesStatsRow = z
  .object({
    stat_date: SalesBusinessDate,
    owner_user_id: Uuid,
    tier: CustomerTier,
    currency: SalesCurrency,
    quotes_created: z.number().int().min(0),
    quotes_sent: z.number().int().min(0),
    quotes_accepted: z.number().int().min(0),
    quotes_rejected: z.number().int().min(0),
    quotes_expired: z.number().int().min(0),
    off_list_lines: z.number().int().min(0),
    margin_breach_lines: z.number().int().min(0),
    /** The DENOMINATOR the two counts above are a rate of: every line that
     * carried a price on a sent quote or a confirmed direct order. §2.21 did
     * not have this column until D-293 amendment (f) — without it §4.9's
     * headline "off-list line rate %" is not derivable from the rollup, and
     * half the figure would have to be computed live against the source. */
    priced_lines: z.number().int().min(0),
    orders_confirmed: z.number().int().min(0),
    /** §7: withheld from a reader without `sales.read_value`, as NULL rather
     * than as an absent key — a client must be able to tell "no money today"
     * from "you may not see the money", and a key that comes and goes by role
     * is a wire shape that changes per caller. */
    order_value_minor: SalesMinorMoney.nullable(),
    offcut_value_minor: SalesMinorMoney.nullable(),
  })
  .openapi("SalesStatsRow");

/**
 * `GET /v1/sales-stats` query. `from` and `to` are business dates in the
 * TENANT's zone and both are required: a default window would silently decide
 * what "recently" means for a factory, and the screen always knows its own
 * range. The server bounds the span rather than trusting the client.
 */
export const SalesStatsQuery = z.object({
  from: SalesBusinessDate,
  to: SalesBusinessDate,
  owner_user_id: Uuid.optional(),
  tier: CustomerTier.optional(),
});

export const SalesStatsResponse = z
  .object({
    items: z.array(SalesStatsRow),
    /** True when the caller holds `sales.read_value`. The screen renders the
     * money columns from THIS rather than from "the first row's value is not
     * null", which would read an empty page as a permission answer. */
    values_visible: z.boolean(),
    /** The last day this answer reads from the rollup: the earlier of the day
     * the engine last built the tenant's rollup through and the tenant's
     * yesterday, or null before the engine has built it (D-319). Everything
     * after it in the requested range was computed LIVE from the source
     * tables (§3.10's "today"). The screen states it rather than implying the
     * whole range is equally settled. */
    materialized_through: SalesBusinessDate.nullable(),
  })
  .openapi("SalesStatsResponse");

// ---------------------------------------------------------------------------
// §10.13's DRILL-THROUGH — the off-list lines behind the rate.
//
// The rollup answers "40%". §10.13 asks the next question, which is the only
// one a sales lead can act on: WHICH lines, and what did the manager type
// instead of the resolved rate. A percentage nobody can open is a number to
// argue about, not a habit anyone can correct.
//
// IT IS ALWAYS A LIVE READ, never the rollup — `sales_stats_daily` holds
// counts, and a count cannot name a line. The two arms are EXACTLY the two
// tier-discipline arms of the rebuild's own union (a sent quote's sent
// revision, and a confirmed direct order's live lines) with `off_list` added,
// which is what makes the drill-through's row count equal the table's
// `off_list_lines` over the same range and key. That equality is asserted,
// not assumed.
//
// NO PRODUCT NAME, deliberately. `products` is module 01's table and this
// module does not read it (the constitution's cross-module rule). The line's
// OWN `description` is module 09's column, written when the line was
// composed, and it is what the document itself says — which is the more
// honest answer anyway: a product renamed since the send would otherwise
// re-label a historical line.
// ---------------------------------------------------------------------------

export const SalesOffListLine = z
  .object({
    /** Which document the line sits on. The two are one list because a
     * manager's off-list habit does not care which door they used, and
     * §2.21 counts both for the same reason. */
    document_kind: z.enum(["quote", "order"]),
    document_id: Uuid,
    document_no: z.string().min(1).max(40),
    line_no: z.number().int().min(1),
    /** The day the line counted on — the send for a quote, the confirmation
     * for a direct order, in the tenant's zone. The same stamp §2.21 dates
     * the counter by, so a line here falls in the same day as the count it
     * came from. */
    dated_on: SalesBusinessDate,
    owner_user_id: Uuid,
    tier: CustomerTier,
    currency: SalesCurrency,
    product_id: Uuid,
    /** The line's own text, as the document says it (see the note above). */
    description: z.string(),
    qty: SalesQty,
    /** THE RESOLVED RATE the §3.2 engine answered, and the rate the manager
     * TYPED instead. Both §7 money, ABSENT without `sales.read_value`, the
     * house spelling of redaction (D-337 ruling 1; until D-337 they were
     * nulled). `list_price_minor` stays nullable for its own reason (0055's
     * amendment): a line composed where no list rate resolved has no
     * resolved price to compare against, and the screen says so rather than
     * printing a difference against zero. */
    list_price_minor: SalesMinorMoney.nullable().optional(),
    unit_price_minor: SalesMinorMoney.optional(),
    /** Off-list and BREACHING are different facts (§3.2 step 6 vs the margin
     * guard): every row here is off-list, and this says which of them also
     * went under the guard. */
    margin_breached: z.boolean(),
  })
  .openapi("SalesOffListLine");

/** `GET /v1/sales-stats/off-list-lines` query — the same range and narrowing
 * as the rollup read, because it is the same question one level down. */
export const SalesOffListQuery = z.object({
  from: SalesBusinessDate,
  to: SalesBusinessDate,
  owner_user_id: Uuid.optional(),
  tier: CustomerTier.optional(),
});

export const SalesOffListResponse = z
  .object({
    items: z.array(SalesOffListLine),
    /** True when the caller holds `sales.read_value` — the same flag, read
     * the same way, as the rollup response's. */
    values_visible: z.boolean(),
    /** TRUE when the range held more lines than the server returned. NOT a
     * silent cap: a drill-through that quietly stopped at N would tell a
     * sales lead they had found every off-list line when they had not, which
     * is the one thing this list must never do. */
    truncated: z.boolean(),
  })
  .openapi("SalesOffListResponse");
